import type {
  DataSourceDefinition,
  BindingContextTypeDef,
  DataSourceDependencies,
  TypeReferenceBuilder,
  BindingContextDef,
  BindablePropertiesDef,
  PageBindingContextDef,
  ListBindingContextDef,
  BindingContextRelationshipDef,
  RuntimeInfo,
  QueryPaths,
} from '@packages/systems/dynamo/source-types';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';

import {qualify} from '@packages/systems/wfdl-Qualified';
import {Plugins, Component} from '@packages/systems/wfdl';
import {SEARCH_RESULT_BINDING_CONTEXT_EXTERNAL_KEY} from '@packages/systems/core/constants/SiteSearch';

import {
  getSearchResultsLengthFromElement,
  getSearchSnippetLengthFromElement,
  getSearchPreviewQueryFromElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';

const SEARCH_PAGE_BINDING_CONTEXT_ID = 'search-page';
const SEARCH_PAGE_TYPE_ID = 'search-page-type';

const SEARCH_RESULT_BINDING_CONTEXT_ID = 'search-result-item';
const SEARCH_RESULT_TYPE_ID = 'search-result-item-type';

export const dataSourceDefinition: DataSourceDefinition = {
  pluginName: Plugins.name('Search'),

  defineTypes(_dependencies: DataSourceDependencies) {
    const searchPage: BindingContextTypeDef = {
      entity: 'PageRoot',
      id: SEARCH_PAGE_TYPE_ID,
      label: 'Search Page',
      iconName: 'search',
    };

    const searchResult: BindingContextTypeDef = {
      entity: 'Entity',
      id: SEARCH_RESULT_TYPE_ID,
      label: 'Search Result',
      iconName: 'search',
      externalKey: SEARCH_RESULT_BINDING_CONTEXT_EXTERNAL_KEY,
    };

    return [searchPage, searchResult];
  },

  defineBindableProperties<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindablePropertiesDef<EntityTRef, ListTRef>[] {
    const searchResultProperties: BindablePropertiesDef<EntityTRef, ListTRef> =
      {
        type: referenceBuilder.entityOf({
          entity: 'Entity',
          id: SEARCH_RESULT_TYPE_ID,
        }),
        bindableProperties: [
          {
            id: 'page-url',
            label: 'Page URL',
            type: 'Link',
            path: [{id: '', slug: 'page-url', type: 'Link'}],
            queryPath: [],
            meta: {
              required: true,
            },
          },
          {
            id: 'description',
            label: 'Search Description',
            type: 'PlainText',
            path: [{id: '', slug: 'description', type: 'PlainText'}],
            queryPath: [],
            meta: {
              singleLine: false,
              minLength: null,
              maxLength: null,
              required: true,
            },
          },
          {
            id: 'title',
            label: 'Search Title',
            type: 'PlainText',
            path: [{id: '', slug: 'title', type: 'PlainText'}],
            queryPath: [],
            meta: {
              singleLine: true,
              minLength: null,
              maxLength: null,
              required: true,
            },
          },
          {
            id: 'highlighted-snippet',
            label: 'Snippet',
            type: 'HighlightedText',
            path: [
              {id: '', slug: 'highlighted-snippet', type: 'HighlightedText'},
            ],
            queryPath: [],
            meta: {
              required: true,
            },
          },

          {
            id: 'image',
            label: 'Search Image',
            type: 'ImageRef',
            path: [{id: '', slug: 'image', type: 'ImageRef'}],
            queryPath: [],
            meta: {
              required: true,
            },
          },
        ],
      };

    return [searchResultProperties];
  },

  defineBindingContexts<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
    const searchPage: PageBindingContextDef<EntityTRef> = {
      variant: 'Page',
      id: SEARCH_PAGE_BINDING_CONTEXT_ID,
      label: 'Search Index',
      type: referenceBuilder.entityOf({
        entity: 'PageRoot',
        id: SEARCH_PAGE_TYPE_ID,
      }),
      matchPage: (page) => page.utilKey === 'search',
    };

    const searchResultWrapper: ListBindingContextDef<ListTRef> = {
      variant: 'List',
      id: SEARCH_RESULT_BINDING_CONTEXT_ID,
      label: 'Search Index',
      type: referenceBuilder.listOf({
        entity: 'Entity',
        id: SEARCH_RESULT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Search'),
          Component.name('SearchResultWrapper')
        ),

        matcher: (_element) => true,
      },
      query: {
        queryPathPrefix: [],
        boundaryCreatorQueryPaths: specifySearchResultWrapperQueryPaths,
      },
    };

    return [searchPage, searchResultWrapper];
  },

  defineRelationships<RootTRef, EntityTRef, ListTRef>(
    _dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindingContextRelationshipDef<RootTRef, EntityTRef>[] {
    return [
      {
        type: referenceBuilder.entityOf({
          entity: 'PageRoot',
          id: SEARCH_PAGE_TYPE_ID,
        }),
        relativeContexts: [SEARCH_RESULT_BINDING_CONTEXT_ID],
      },
    ];
  },
};

function specifySearchResultWrapperQueryPaths(
  element: ExpressionElement,
  runtimeInfo: RuntimeInfo
): QueryPaths {
  const {environment, showSampleSearchResults} = runtimeInfo;
  const isDesigner = environment === WebflowEnvironments.DESIGNER;
  const shouldSkip = isDesigner && showSampleSearchResults;

  if (shouldSkip) {
    return [];
  }

  const resultsLength = getSearchResultsLengthFromElement(element);
  const snippetLength = getSearchSnippetLengthFromElement(element);
  const previewQuery = getSearchPreviewQueryFromElement(element);
  const random = isDesigner && !previewQuery;

  return [
    [
      `${searchFieldAlias(element.id)}: search(query: ${
        isDesigner ? `"${previewQuery}"` : '$query'
      }, random: ${
        random ? 'true' : 'false'
      }, publishTarget: $publishTarget, first: ${resultsLength}, snippetLength: ${snippetLength})`,
    ],
  ];
}

export const searchFieldAlias = (nodeId: string) =>
  `search_${nodeId.replace(/-/g, '_')}`;
